import { faArrowDown, faArrowUp, faCopy, faEdit, faEye, faEyeSlash, faTrash } from '@fortawesome/free-solid-svg-icons';

import { Column, DataTable, DataTableContainer, TableActionButton } from 'components/DataTable';
import { useApi } from '../../../hooks/useApi';
import { useConfirmedAction } from 'hooks/useConfirmedAction';
import { useModal } from 'hooks/useModal';
import { useCallback, useRef } from 'react';
import { SpaceForm } from '.';
import { Space } from '../types';
import { Button } from '@ui';

interface Props {
    clubSlug: string;
}

export const SpaceList = ({ clubSlug }: Props) => {
    const confirm = useConfirmedAction({
        message: (
            <>
                <p>Möchtest du diesen Platz löschen?</p>
                <div className="alert alert-warning">
                    <strong>Achtung!</strong> Wenn du den Platz löscht, werden alle aktiven Buchungen storniert!
                </div>
            </>
        )
    });
    const [setModal, resetModal] = useModal();
    const asyncList = useRef<any>(undefined);
    const api = useApi();

    const handleShowSpaceForm = useCallback(
        (space?: Space) =>
            setModal({
                title: space ? `Platz "${space.name}" bearbeiten` : 'Platz erstellen',
                content: (
                    <SpaceForm
                        clubSlug={clubSlug}
                        space={space}
                        callback={() => {
                            asyncList.current?.reload?.();
                            resetModal();
                        }}
                    />
                ),
                show: true,
                size: 60
            }),
        []
    );

    const handleToggleSpace = useCallback((space: Space) => {
        void api({
            url: `space/${space.slug}/toggle`,
            onSuccess: () => asyncList.current?.reload?.()
        });
    }, []);

    const handleDeleteSpace = useCallback((space: Space) => {
        api({
            url: `space/${space.slug}`,
            method: 'DELETE',
            onSuccess: () => asyncList.current?.reload?.()
        });
    }, []);

    const handleDuplicateSpace = useCallback((space: Space) => {
        api({
            url: `space/${space.slug}/duplicate`,
            onSuccess: () => asyncList.current?.reload?.()
        });
    }, []);

    const handleMoveSpace = useCallback((space: Space, up: boolean) => {
        // NOTE: We are assuming sorted input from the DB here
        const spaces = asyncList.current?.items as Space[];

        // Figure out where in the list the targeted space lies
        const targetIndex = spaces.findIndex((s) => s.slug === space.slug);

        space = spaces[targetIndex];

        if ((targetIndex >= spaces.length - 1 && !up) || (targetIndex === 0 && up)) {
            // Since we cannot move an object out of bounds, we can nop these calls here
            return;
        }

        // Switch positions with the competing space
        const swappingSpace = spaces[targetIndex + (up ? -1 : 1)];

        // Set the target space to the old position of the space above
        api({
            url: `space/${space.slug}/swap/${swappingSpace.slug}`,
            method: 'PUT',
            muted: true,
            onSuccess: () => asyncList.current?.reload?.()
        });
    }, []);

    const columns: Column[] = [
        {
            uid: 'name',
            name: 'Name'
        },
        {
            uid: 'space_group.name',
            name: 'Gruppe'
        },
        {
            uid: 'actions',
            name: 'Optionen',
            align: 'end'
        }
    ];

    return (
        <DataTableContainer>
            <Button className="tw-py-5" onPress={() => handleShowSpaceForm()}>
                Platz erstellen
            </Button>
            <DataTable
                className="tw-flex-auto"
                tableProps={{
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false
                }}
                hideSearchBar
                asyncListRef={asyncList}
                columns={columns}
                fetchUrl={`space/${clubSlug}`}
                sortingFn={(a, b) => a.position - b.position}
                rowActions={(item) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton
                            icon={item.visible ? faEye : faEyeSlash}
                            color={item.visible ? 'success' : 'warning'}
                            onPress={() => handleToggleSpace(item)}
                        />
                        <TableActionButton icon={faArrowDown} onPress={() => handleMoveSpace(item, false)} />
                        <TableActionButton icon={faArrowUp} onPress={() => handleMoveSpace(item, true)} />
                        <TableActionButton icon={faCopy} onPress={() => handleDuplicateSpace(item)} />
                        <TableActionButton icon={faEdit} onPress={() => handleShowSpaceForm(item)} />
                        <TableActionButton icon={faTrash} onPress={() => confirm(() => handleDeleteSpace(item))} />
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default SpaceList;
